import React, { ReactNode } from 'react';
import ListingCard from 'components/listing-card';
import ListingCardCarousel from 'components/listing-card-carousel';
import { buildClassName } from 'utils/build-class-name';
import LoadingSkeleton from 'components/loading-skeleton';
import styles from './style.module.scss';
import {
  GTM_CLICK_ADDRESS_PAGE_COMPARABLE_LISTING_BOTTOM, GTM_CLICK_APPRAISAL_NO_ESTIMATE_SIMILAR_AVAILABLE_CARD,
  GTM_CLICK_APPRAISAL_NO_ESTIMATE_SIMILAR_SOLD_CARD,
  GTM_CLICK_APPRAISAL_SIMILAR_AVAILABLE_CARD,
  GTM_CLICK_APPRAISAL_SIMILAR_SOLD_CARD,
  GTM_CLICK_LISTING_SIMILAR,
} from 'constants/events';
import { useIsMobile } from 'hooks/use-size-class';
import formatCurrency from 'utils/format-currency';

import type { SimilarListingCardData } from 'components/dynamic-page/listing-page/similar-listings-sections/similar_listing_card_data';
import type Listing from 'data/listing';
import type { OnListingCardCLickProps } from 'utils/listing-card';

interface Props {
  heading: string | ReactNode;
  listings: SimilarListingCardData[] | Listing[] | null[] ;
  isLoading: boolean;
  listingToCompare?: SimilarListingCardData| Listing;
  hasNavigatedFromInternalLink?: boolean;
  isTop?: boolean;
  noEstimate?: boolean;
  showCarousel?: boolean;
  componentUsage: SimilarListingsComponentUsage;
  showPriceRange?: boolean;
  onListingCardClick?: (props: OnListingCardCLickProps) => void;
}

export enum SimilarListingsComponentUsage {
  Similar = 'Similar',
  Comparable = 'Comparable',
  HomeAppraisalSold = 'Home Appraisal Sold',
  HomeAppraisalAvailable = 'Home Appraisal Available'
}

export default function SimilarListings({ heading, listings, isLoading, listingToCompare, hasNavigatedFromInternalLink, isTop, noEstimate, showCarousel, componentUsage, showPriceRange, onListingCardClick }: Props) {
  const lessThan3Listings = listings.length < 3;
  const isSimilar = componentUsage===SimilarListingsComponentUsage.Similar;
  const type = isSimilar ? 'similar' : undefined;
  const hideGrid = (isSimilar && !hasNavigatedFromInternalLink && isTop) || showCarousel;
  const isMobile = useIsMobile();
  const isHomeAppraisalForSale = componentUsage === SimilarListingsComponentUsage.HomeAppraisalAvailable;
  let minPrice, maxPrice = '$0';

  if (showPriceRange) {
    const prices = getMinAndMaxPrice(listings, isHomeAppraisalForSale);
    minPrice = prices[0];
    maxPrice = prices[1];
  }

  let gtmEventName = '';
  switch (componentUsage) {
    case SimilarListingsComponentUsage.Similar:
      gtmEventName = GTM_CLICK_LISTING_SIMILAR;
      break;
    case SimilarListingsComponentUsage.Comparable:
      gtmEventName = GTM_CLICK_ADDRESS_PAGE_COMPARABLE_LISTING_BOTTOM;
      break;
    case SimilarListingsComponentUsage.HomeAppraisalAvailable:
      gtmEventName = noEstimate ? GTM_CLICK_APPRAISAL_NO_ESTIMATE_SIMILAR_AVAILABLE_CARD : GTM_CLICK_APPRAISAL_SIMILAR_AVAILABLE_CARD;
      break;
    case SimilarListingsComponentUsage.HomeAppraisalSold:
      gtmEventName = noEstimate ? GTM_CLICK_APPRAISAL_NO_ESTIMATE_SIMILAR_SOLD_CARD : GTM_CLICK_APPRAISAL_SIMILAR_SOLD_CARD;
      break;
    default:
      gtmEventName = '';
  }

  return (
    <section>
      <h3 className={styles.heading}>{heading}</h3>
      {isMobile && showPriceRange &&
        <h4 className={styles['price-range']}>{isHomeAppraisalForSale ? 'Currently listed between ' : 'Sold between '}
          <span className={styles['bold-price']}>{minPrice}</span> to <span className={styles['bold-price']}>{maxPrice}</span>
        </h4>
      }
      {!hideGrid &&
        <div className={buildClassName(styles['similar-listings'], lessThan3Listings && styles['minimal-listings'])}>
          {listings.map((listing, index) =>
            <div key={index} className={styles['card-wrapper']}>
              {isLoading
                ? <LoadingSkeleton height={'396px'} width={'370px'} />
                : <ListingCard listing={listing as Listing} listingToCompare={listingToCompare} gtmOnClickEvent={gtmEventName} onListingCardClick={onListingCardClick}/>
              }
            </div>
          )}
        </div>
      }
      <div className={buildClassName((showCarousel || (isSimilar && !hasNavigatedFromInternalLink)) ? undefined : styles['mobile-carousel'])}>
        {!isLoading && <ListingCardCarousel listingCardsData={{ listings: listings as SimilarListingCardData[], type }} listingToCompare={listingToCompare as SimilarListingCardData} gtmOnClickEvent={gtmEventName} />}
        {isLoading && <LoadingSkeleton height={'396px'} />}
      </div>
    </section>
  );
}

function getMinAndMaxPrice(listings: SimilarListingCardData[] | Listing[], forSale: boolean) {
  let minPrice, maxPrice;
  
  for (const listing of listings) {
    if (!listing) continue;
    const listingPrice = forSale ? listing.price : listing.soldPrice;
    if (!minPrice || listingPrice < minPrice) {
      minPrice = listingPrice;
    }
    if (!maxPrice || listingPrice > maxPrice) {
      maxPrice = listingPrice;
    }
  }

  return [formatCurrency(minPrice), formatCurrency(maxPrice)];
}
